export interface ChiropracticMetric {
  timestamp: number;
  muscleTensionScore?: number; // 0-100
  tissueElasticity?: number; // mm
  triggerPointIntensity?: number; // 0-10
  rangeOfMotion?: number; // degrees
  painResponse?: number; // 0-10
  treatmentEffectiveness?: number; // 0-100
}

export interface ChiropracticSession {
  id: string;
  patientId: number;
  startTime: string;
  endTime: string;
  duration: number;
  metrics: ChiropracticMetric[];
  notes: string;
  selectedMetrics: string[];
}

export interface ChiropracticPatient {
  id: number;
  first_name: string;
  last_name: string;
  diagnosis: string;
  sessions_count: number;
  last_visit: string;
}

// Generate dummy patients
export const generateDummyPatients = (): ChiropracticPatient[] => [
  { id: 1, first_name: 'John', last_name: 'Smith', diagnosis: 'Lower back pain', sessions_count: 8, last_visit: '2024-01-15' },
  { id: 2, first_name: 'Sarah', last_name: 'Johnson', diagnosis: 'Neck tension', sessions_count: 5, last_visit: '2024-01-12' },
  { id: 3, first_name: 'Mike', last_name: 'Davis', diagnosis: 'Shoulder stiffness', sessions_count: 12, last_visit: '2024-01-18' },
  { id: 4, first_name: 'Emma', last_name: 'Wilson', diagnosis: 'Chronic lower back pain', sessions_count: 15, last_visit: '2024-01-20' },
  { id: 5, first_name: 'Robert', last_name: 'Brown', diagnosis: 'Cervical spine dysfunction', sessions_count: 7, last_visit: '2024-01-14' },
  { id: 6, first_name: 'Lisa', last_name: 'Garcia', diagnosis: 'Thoracic outlet syndrome', sessions_count: 9, last_visit: '2024-01-16' },
];

// Generate dummy session data
export const generateDummySession = (patientId: number): ChiropracticSession => {
  const metrics: ChiropracticMetric[] = [];
  const startTime = new Date();
  
  // Generate 10 metric samples over 30 minutes
  for (let i = 0; i < 10; i++) {
    metrics.push({
      timestamp: startTime.getTime() + (i * 3 * 60 * 1000), // Every 3 minutes
      muscleTensionScore: Math.random() * 40 + 30, // 30-70
      tissueElasticity: Math.random() * 3 + 2, // 2-5 mm
      triggerPointIntensity: Math.random() * 4 + 3, // 3-7
      rangeOfMotion: Math.random() * 20 + 70, // 70-90 degrees
      painResponse: Math.random() * 3 + 2, // 2-5
      treatmentEffectiveness: Math.random() * 30 + 60, // 60-90%
    });
  }
  
  return {
    id: `session_${Date.now()}`,
    patientId,
    startTime: startTime.toISOString(),
    endTime: new Date(startTime.getTime() + 30 * 60 * 1000).toISOString(),
    duration: 30 * 60, // 30 minutes in seconds
    metrics,
    notes: 'Patient reported initial tension in target area. Applied soft tissue treatment with progressive pressure.',
    selectedMetrics: ['muscleTensionScore', 'treatmentEffectiveness', 'painResponse', 'rangeOfMotion'],
  };
};

// Extended data for patient comparison functionality
export interface ChiropracticPatientDemographics {
  id: number;
  age: number;
  gender: 'male' | 'female' | 'other';
  height: number; // in cm
  weight: number; // in kg
  injury: string;
  diagnosis: string;
  dxCode: string;
  symptomsOnset?: string;
  previousTreatment?: string;
  clinic: number;
}

export interface ChiropracticMetricSnapshot {
  timestamp: string;
  sessionNumber: number;
  weeksFromBaseline: number;
  muscleTensionScore: number;
  tissueElasticity: number;
  triggerPointIntensity: number;
  rangeOfMotion: number;
  painResponse: number;
  treatmentEffectiveness: number;
  comfortLevel: number; // 0-10 scale
  perceivedImprovement: number; // percentage
}

export interface ChiropracticPatientData {
  demographics: ChiropracticPatientDemographics;
  metrics: ChiropracticMetricSnapshot[];
}

// Common chiropractic conditions and diagnoses
const chiropracticConditions = {
  spine: [
    { injury: 'Lower Back Pain', diagnosis: 'Lumbar Strain', dxCode: 'M54.5' },
    { injury: 'Neck Pain', diagnosis: 'Cervical Strain', dxCode: 'M54.2' },
    { injury: 'Herniated Disc', diagnosis: 'Lumbar Disc Herniation', dxCode: 'M51.26' },
    { injury: 'Sciatica', diagnosis: 'Lumbar Radiculopathy', dxCode: 'M54.16' },
    { injury: 'Whiplash', diagnosis: 'Cervical Sprain', dxCode: 'S13.4' }
  ],
  extremities: [
    { injury: 'Shoulder Impingement', diagnosis: 'Shoulder Impingement Syndrome', dxCode: 'M75.3' },
    { injury: 'Frozen Shoulder', diagnosis: 'Adhesive Capsulitis', dxCode: 'M75.0' },
    { injury: 'Tennis Elbow', diagnosis: 'Lateral Epicondylitis', dxCode: 'M77.1' },
    { injury: 'Carpal Tunnel', diagnosis: 'Carpal Tunnel Syndrome', dxCode: 'G56.0' }
  ],
  chronic: [
    { injury: 'Chronic Pain', diagnosis: 'Chronic Pain Syndrome', dxCode: 'G89.4' },
    { injury: 'Fibromyalgia', diagnosis: 'Fibromyalgia', dxCode: 'M79.3' },
    { injury: 'Tension Headaches', diagnosis: 'Tension-Type Headache', dxCode: 'G44.2' }
  ]
};

// Function to create realistic chiropractic patient metrics progression
function generateChiropracticPatientMetrics(demographics: ChiropracticPatientDemographics, numberOfSessions: number): ChiropracticMetricSnapshot[] {
  const metrics: ChiropracticMetricSnapshot[] = [];
  const hasSpinalIssue = demographics.injury.toLowerCase().includes('back') || demographics.injury.toLowerCase().includes('neck');
  const hasExtremityIssue = demographics.injury.toLowerCase().includes('shoulder') || demographics.injury.toLowerCase().includes('elbow');
  
  // Initial baseline metrics based on condition type
  let baselineMuscleTension = 0;
  let baselineTissueElasticity = 0;
  let baselineTriggerPointIntensity = 0;
  let baselineRangeOfMotion = 0;
  let baselinePainResponse = 0;
  let baselineTreatmentEffectiveness = 0;
  let baselineComfortLevel = 0;
  let baselinePerceivedImprovement = 0;
  
  // Set realistic baseline values based on condition
  if (hasSpinalIssue) {
    baselineMuscleTension = Math.random() * 20 + 60; // 60-80 (high tension)
    baselineTissueElasticity = Math.random() * 1 + 1.5; // 1.5-2.5 mm (low elasticity)
    baselineTriggerPointIntensity = Math.random() * 3 + 6; // 6-9 (high intensity)
    baselineRangeOfMotion = Math.random() * 15 + 45; // 45-60 degrees (limited)
    baselinePainResponse = Math.random() * 3 + 6; // 6-9 (high pain)
    baselineTreatmentEffectiveness = Math.random() * 20 + 30; // 30-50% (low effectiveness initially)
    baselineComfortLevel = Math.random() * 3 + 2; // 2-5 (low comfort)
    baselinePerceivedImprovement = Math.random() * 15 + 5; // 5-20% (minimal improvement)
  } else if (hasExtremityIssue) {
    baselineMuscleTension = Math.random() * 15 + 50; // 50-65
    baselineTissueElasticity = Math.random() * 1.5 + 2; // 2-3.5 mm
    baselineTriggerPointIntensity = Math.random() * 2 + 5; // 5-7
    baselineRangeOfMotion = Math.random() * 20 + 60; // 60-80 degrees
    baselinePainResponse = Math.random() * 2 + 5; // 5-7
    baselineTreatmentEffectiveness = Math.random() * 25 + 40; // 40-65%
    baselineComfortLevel = Math.random() * 2 + 4; // 4-6
    baselinePerceivedImprovement = Math.random() * 20 + 10; // 10-30%
  } else {
    // Chronic conditions
    baselineMuscleTension = Math.random() * 25 + 70; // 70-95 (very high)
    baselineTissueElasticity = Math.random() * 0.5 + 1; // 1-1.5 mm (very low)
    baselineTriggerPointIntensity = Math.random() * 2 + 7; // 7-9 (very high)
    baselineRangeOfMotion = Math.random() * 10 + 40; // 40-50 degrees (very limited)
    baselinePainResponse = Math.random() * 2 + 7; // 7-9 (very high pain)
    baselineTreatmentEffectiveness = Math.random() * 15 + 25; // 25-40% (low effectiveness)
    baselineComfortLevel = Math.random() * 2 + 1; // 1-3 (very low comfort)
    baselinePerceivedImprovement = Math.random() * 10 + 5; // 5-15% (very minimal)
  }
  
  // Target values: what we expect after successful treatment
  const targetMuscleTension = Math.random() * 10 + 15; // 15-25 (low tension)
  const targetTissueElasticity = Math.random() * 1 + 4; // 4-5 mm (good elasticity)
  const targetTriggerPointIntensity = Math.random() * 2 + 1; // 1-3 (low intensity)
  const targetRangeOfMotion = Math.random() * 10 + 85; // 85-95 degrees (good mobility)
  const targetPainResponse = Math.random() * 2 + 0; // 0-2 (minimal pain)
  const targetTreatmentEffectiveness = Math.random() * 15 + 80; // 80-95% (high effectiveness)
  const targetComfortLevel = Math.random() * 2 + 8; // 8-10 (high comfort)
  const targetPerceivedImprovement = Math.random() * 15 + 80; // 80-95% (significant improvement)
  
  // Create a progression of metrics that gradually improve
  for (let i = 0; i < numberOfSessions; i++) {
    const progress = i / (numberOfSessions - 1); // 0 to 1
    
    // Add some noise to make data realistic
    const noise = () => (Math.random() - 0.5) * 0.1;
    const weeksBetweenSessions = Math.floor(Math.random() * 2) + 1; // 1-2 weeks between sessions
    const weeksFromBaseline = i * weeksBetweenSessions;
    
    // Calculate current metrics with progressive improvement
    const currentMuscleTension = baselineMuscleTension - (baselineMuscleTension - targetMuscleTension) * (progress + noise());
    const currentTissueElasticity = baselineTissueElasticity + (targetTissueElasticity - baselineTissueElasticity) * (progress + noise());
    const currentTriggerPointIntensity = baselineTriggerPointIntensity - (baselineTriggerPointIntensity - targetTriggerPointIntensity) * (progress + noise());
    const currentRangeOfMotion = baselineRangeOfMotion + (targetRangeOfMotion - baselineRangeOfMotion) * (progress + noise());
    const currentPainResponse = baselinePainResponse - (baselinePainResponse - targetPainResponse) * (progress + noise());
    const currentTreatmentEffectiveness = baselineTreatmentEffectiveness + (targetTreatmentEffectiveness - baselineTreatmentEffectiveness) * (progress + noise());
    const currentComfortLevel = baselineComfortLevel + (targetComfortLevel - baselineComfortLevel) * (progress + noise());
    const currentPerceivedImprovement = baselinePerceivedImprovement + (targetPerceivedImprovement - baselinePerceivedImprovement) * (progress + noise());
    
    // Create the metric snapshot
    metrics.push({
      timestamp: new Date(Date.now() - (weeksFromBaseline * 7 * 24 * 60 * 60 * 1000)).toISOString(),
      sessionNumber: i + 1,
      weeksFromBaseline,
      muscleTensionScore: Math.max(0, Math.min(100, Math.round(currentMuscleTension * 10) / 10)),
      tissueElasticity: Math.max(0, Math.round(currentTissueElasticity * 100) / 100),
      triggerPointIntensity: Math.max(0, Math.min(10, Math.round(currentTriggerPointIntensity * 10) / 10)),
      rangeOfMotion: Math.max(0, Math.min(180, Math.round(currentRangeOfMotion))),
      painResponse: Math.max(0, Math.min(10, Math.round(currentPainResponse * 10) / 10)),
      treatmentEffectiveness: Math.max(0, Math.min(100, Math.round(currentTreatmentEffectiveness * 10) / 10)),
      comfortLevel: Math.max(0, Math.min(10, Math.round(currentComfortLevel * 10) / 10)),
      perceivedImprovement: Math.max(0, Math.min(100, Math.round(currentPerceivedImprovement * 10) / 10))
    });
  }
  
  return metrics;
}

// Generate a single chiropractic patient with realistic progression data
export function generateChiropracticPatient(id: number): ChiropracticPatientData {
  // Random patient demographics
  const age = Math.floor(Math.random() * 50) + 25; // 25-75 years
  const gender = Math.random() > 0.5 ? 'male' : 'female';
  const height = Math.floor(Math.random() * 30) + 160; // 160-190 cm
  const weight = Math.floor(Math.random() * 50) + 60; // 60-110 kg
  const clinic = Math.floor(Math.random() * 5) + 1; // 5 clinics
  
  // Select random condition category and specific condition
  const conditionCategories = Object.keys(chiropracticConditions);
  const category = conditionCategories[Math.floor(Math.random() * conditionCategories.length)];
  const conditionData = chiropracticConditions[category as keyof typeof chiropracticConditions][
    Math.floor(Math.random() * chiropracticConditions[category as keyof typeof chiropracticConditions].length)
  ];
  
  // Number of sessions (chiropractic patients typically have 8-16 sessions)
  const numberOfSessions = Math.floor(Math.random() * 8) + 8;
  
  // Create patient demographics
  const demographics: ChiropracticPatientDemographics = {
    id,
    age,
    gender,
    height,
    weight,
    injury: conditionData.injury,
    diagnosis: conditionData.diagnosis,
    dxCode: conditionData.dxCode,
    symptomsOnset: `${Math.floor(Math.random() * 12) + 1} months ago`,
    previousTreatment: Math.random() > 0.5 ? 'Physical therapy, NSAIDs' : 'NSAIDs only',
    clinic
  };
  
  // Generate metrics data for the patient
  const metrics = generateChiropracticPatientMetrics(demographics, numberOfSessions);
  
  return {
    demographics,
    metrics
  };
}

// Generate the full dataset for comparison analytics
let fullChiropracticDataset: ChiropracticPatientData[] = [];

export function generateFullChiropracticDataset(count: number = 200): ChiropracticPatientData[] {
  if (fullChiropracticDataset.length === 0) {
    console.log("Generating chiropractic comparison dataset...");
    fullChiropracticDataset = Array.from({ length: count }, (_, i) => generateChiropracticPatient(i + 1));
    console.log(`Generated ${count} chiropractic patient records for comparison`);
  }
  return fullChiropracticDataset;
}

// Function to find similar chiropractic patients
export function findSimilarChiropracticPatients(
  patient: ChiropracticPatientDemographics, 
  allPatients: ChiropracticPatientData[],
  options: {
    ageRange?: number,
    matchGender?: boolean,
    matchDiagnosis?: boolean,
    matchSymptomArea?: boolean
  } = {
    ageRange: 5,
    matchGender: true,
    matchDiagnosis: true,
    matchSymptomArea: false
  }
): ChiropracticPatientData[] {
  const {
    ageRange = 5,
    matchGender = true,
    matchDiagnosis = true,
    matchSymptomArea = false
  } = options;
  
  return allPatients.filter(p => {
    const ageMatch = Math.abs(p.demographics.age - patient.age) <= ageRange;
    const genderMatch = !matchGender || p.demographics.gender === patient.gender;
    const diagnosisMatch = !matchDiagnosis || p.demographics.diagnosis === patient.diagnosis;
    const symptomAreaMatch = !matchSymptomArea || p.demographics.injury.includes(patient.injury.split(' ')[0]);
    
    return ageMatch && genderMatch && (diagnosisMatch || symptomAreaMatch) && p.demographics.id !== patient.id;
  });
}

// Get aggregated chiropractic metrics for a group of patients
export function getAggregatedChiropracticMetrics(patients: ChiropracticPatientData[]): { 
  byWeek: Record<number, { 
    muscleTensionScore: number,
    tissueElasticity: number,
    triggerPointIntensity: number,
    rangeOfMotion: number,
    painResponse: number,
    treatmentEffectiveness: number,
    comfortLevel: number,
    perceivedImprovement: number,
    count: number 
  }> 
} {
  const aggregated: Record<number, {
    muscleTensionScore: number,
    tissueElasticity: number,
    triggerPointIntensity: number,
    rangeOfMotion: number,
    painResponse: number,
    treatmentEffectiveness: number,
    comfortLevel: number,
    perceivedImprovement: number,
    count: number
  }> = {};
  
  patients.forEach(patient => {
    patient.metrics.forEach(metric => {
      const week = metric.weeksFromBaseline;
      
      if (!aggregated[week]) {
        aggregated[week] = {
          muscleTensionScore: 0,
          tissueElasticity: 0,
          triggerPointIntensity: 0,
          rangeOfMotion: 0,
          painResponse: 0,
          treatmentEffectiveness: 0,
          comfortLevel: 0,
          perceivedImprovement: 0,
          count: 0
        };
      }
      
      aggregated[week].muscleTensionScore += metric.muscleTensionScore;
      aggregated[week].tissueElasticity += metric.tissueElasticity;
      aggregated[week].triggerPointIntensity += metric.triggerPointIntensity;
      aggregated[week].rangeOfMotion += metric.rangeOfMotion;
      aggregated[week].painResponse += metric.painResponse;
      aggregated[week].treatmentEffectiveness += metric.treatmentEffectiveness;
      aggregated[week].comfortLevel += metric.comfortLevel;
      aggregated[week].perceivedImprovement += metric.perceivedImprovement;
      aggregated[week].count += 1;
    });
  });
  
  // Calculate averages
  Object.keys(aggregated).forEach(weekStr => {
    const week = parseInt(weekStr);
    const data = aggregated[week];
    
    if (data.count > 0) {
      data.muscleTensionScore = Math.round((data.muscleTensionScore / data.count) * 10) / 10;
      data.tissueElasticity = Math.round((data.tissueElasticity / data.count) * 100) / 100;
      data.triggerPointIntensity = Math.round((data.triggerPointIntensity / data.count) * 10) / 10;
      data.rangeOfMotion = Math.round((data.rangeOfMotion / data.count) * 10) / 10;
      data.painResponse = Math.round((data.painResponse / data.count) * 10) / 10;
      data.treatmentEffectiveness = Math.round((data.treatmentEffectiveness / data.count) * 10) / 10;
      data.comfortLevel = Math.round((data.comfortLevel / data.count) * 10) / 10;
      data.perceivedImprovement = Math.round((data.perceivedImprovement / data.count) * 10) / 10;
    }
  });
  
  return { byWeek: aggregated };
}

// Export dummy dataset for development
export const dummyChiropracticDataset = generateFullChiropracticDataset();